import Reveal from '@/components/feature/Reveal';
import { formatSigned, formatBalance } from '@/utils/numberFormat';
import type { YearGroup, MonthRow } from '@/utils/monthlyGroups';

interface MonthlyBreakdownCardsProps {
  yearGroups: YearGroup[];
  totalProfit: number;
  totalPct: number;
  positiveMonths: number;
  monthCount: number;
  closingBalance: number;
  /** When a single year is selected the per-group subtotal would duplicate the footer. */
  showSubtotals?: boolean;
}

function MonthCard({ row }: { row: MonthRow }) {
  const isWin = row.profit >= 0;

  return (
    <div className="rounded-lg border border-background-200 bg-background-50 p-3 sm:p-4">
      <div className="flex items-center justify-between gap-2">
        <span className="font-heading text-sm font-bold text-foreground-950">{row.month}</span>
        <span
          className={`inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[11px] font-semibold ${
            isWin ? 'bg-primary-100 text-primary-700' : 'bg-accent-100 text-accent-900'
          }`}
        >
          <i className={isWin ? 'ri-arrow-up-line' : 'ri-arrow-down-line'} aria-hidden="true"></i>
          {isWin ? 'Win' : 'Loss'}
        </span>
      </div>

      <div className="mt-3 flex items-start justify-between gap-2">
        <div className="flex flex-col gap-0.5">
          <dt className="text-[10px] text-foreground-500">Return</dt>
          <dd
            className={`font-num text-[13px] font-semibold ${isWin ? 'text-primary-600' : 'text-secondary-500'}`}
          >
            {row.pct >= 0 ? '+' : ''}
            {row.pct.toFixed(2)}%
          </dd>
        </div>
        <div className="flex flex-col items-center gap-0.5">
          <dt className="text-[10px] text-foreground-500">P/L</dt>
          <dd
            className={`font-num text-[13px] font-semibold ${isWin ? 'text-primary-600' : 'text-secondary-500'}`}
          >
            {formatSigned(row.profit)}
          </dd>
        </div>
        <div className="flex flex-col items-end gap-0.5">
          <dt className="text-[10px] text-foreground-500">Balance</dt>
          <dd className="font-num text-[13px] font-semibold text-foreground-800">
            {formatBalance(row.balance)}
          </dd>
        </div>
      </div>
    </div>
  );
}

export default function MonthlyBreakdownCards({
  yearGroups,
  totalProfit,
  totalPct,
  positiveMonths,
  monthCount,
  closingBalance,
  showSubtotals = true,
}: MonthlyBreakdownCardsProps) {
  return (
    <div className="mt-4 space-y-6 md:hidden">
      {yearGroups.map((group, groupIndex) => (
        <Reveal key={group.year} delay={Math.min(groupIndex * 60, 180)}>
          <div>
            <div className="flex items-center gap-2.5">
              <span className="inline-flex h-7 w-7 items-center justify-center rounded-md bg-secondary-200/70 text-secondary-900">
                <i className="ri-calendar-line text-sm" aria-hidden="true"></i>
              </span>
              <span className="font-heading text-base font-bold text-foreground-950">{group.year}</span>
              <span className="text-xs text-foreground-500">
                {group.months.length} months · {group.positive} profitable
              </span>
            </div>

            <div className="mt-3 space-y-2.5">
              {group.months.map((row) => (
                <MonthCard key={row.id} row={row} />
              ))}
            </div>

            {showSubtotals ? (
              <div className="mt-2.5 rounded-lg border border-background-300/70 bg-background-100 p-3 sm:p-4">
              <p className="font-heading text-[11px] sm:text-xs font-bold uppercase tracking-wide text-foreground-700">
                {group.year} subtotal
              </p>
              <div className="mt-2.5 flex items-start justify-between gap-2">
                <div className="flex flex-col gap-0.5">
                  <dt className="text-[10px] text-foreground-500">Return</dt>
                  <dd
                    className={`font-num text-[13px] font-bold ${
                      group.compoundedPct >= 0 ? 'text-primary-700' : 'text-secondary-500'
                    }`}
                  >
                    {group.compoundedPct >= 0 ? '+' : ''}
                    {group.compoundedPct.toFixed(1)}%
                  </dd>
                </div>
                <div className="flex flex-col items-center gap-0.5">
                  <dt className="text-[10px] text-foreground-500">P/L</dt>
                  <dd
                    className={`font-num text-[13px] font-bold ${
                      group.profit >= 0 ? 'text-primary-700' : 'text-secondary-500'
                    }`}
                  >
                    {formatSigned(group.profit)}
                  </dd>
                </div>
                <div className="flex flex-col items-end gap-0.5">
                  <dt className="text-[10px] text-foreground-500">Closing</dt>
                  <dd className="font-num text-[13px] font-bold text-foreground-900">
                    {formatBalance(group.closing)}
                  </dd>
                </div>
              </div>
            </div>
            ) : null}
          </div>
        </Reveal>
      ))}

      <Reveal>
        <div className="rounded-lg bg-primary-950 p-3 sm:p-4">
          <p className="font-heading text-sm font-bold text-background-50">
            Total · {monthCount} {monthCount === 1 ? 'month' : 'months'}
          </p>
          <div className="mt-2.5 flex items-start justify-between gap-2">
            <div className="flex flex-col gap-0.5">
              <dt className="text-[10px] text-background-200/70">Return</dt>
              <dd className="font-num text-[13px] font-bold text-background-50">
                {totalPct >= 0 ? '+' : ''}
                {totalPct.toFixed(2)}%
              </dd>
            </div>
            <div className="flex flex-col items-center gap-0.5">
              <dt className="text-[10px] text-background-200/70">P/L</dt>
              <dd className="font-num text-[13px] font-bold text-accent-300">
                {formatSigned(totalProfit)}
              </dd>
            </div>
            <div className="flex flex-col items-end gap-0.5">
              <dt className="text-[10px] text-background-200/70">Closing</dt>
              <dd className="font-num text-[13px] font-bold text-background-50">
                {formatBalance(closingBalance)}
              </dd>
            </div>
          </div>
          <p className="mt-2.5 text-[11px] text-background-200/70">{positiveMonths} profitable months</p>
        </div>
      </Reveal>
    </div>
  );
}